import { ReactNode, useMemo } from 'react';

import { SERVICE_IDS } from '@gasbi/shared/common/features/services';
import { OrganizationApiProvider } from '@gasbi/shared/web/Organization';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import ServiceLogo from '@/components/ServiceLogo';
import { useGetAdminOrganizationQuery } from '@/features/adminOrganization/adminOrganizationApi';
import {
  employeesApi,
  useActivateEmployeeMutation,
  useGetEmployeesAccessibleByOrganizationQuery,
  useGetEmployeesByRoleInfiniteQuery,
  useGetEmployeesByRoleQuery,
} from '@/features/employees';
import {
  mainOrganizationApi,
  roleEmployeesApi,
  useGetMainOrganizationRolesInfiniteQuery,
  useGetRoleEmployeesInfiniteQuery,
  useGetRoleEmployeesListQuery,
} from '@/features/mainOrganization/mainOrganizationApi';
import {
  organizationsApi,
  useGetCachedOrganizationByIdSelector,
  useGetOrganizationRolesInfiniteQuery,
  useGetOrganizationsInfiniteQuery,
} from '@/features/organizations/organizationsApi';
import { permissionsApi } from '@/features/permissions/permissionsApi';
import {
  useAssignRoleMutation,
  useGetCachedRoleByIdSelector,
  useGetRoleByIdQuery,
  useGetSystemRolesQuery,
  useSaveRoleMutation,
  useSuspendRoleMutation,
} from '@/features/roles/hooks';
import {
  useGetRoleAvailablePermissionsByIdQuery,
  useGetRolePermissionsByIdQuery,
} from '@/features/roles/rolesApi';
import { RootRoleId } from '@/features/roles/types';
import { getRoleLabel, getRoleType, isRootRole } from '@/features/roles/utils';
import { useGetServiceEmployeesInfiniteQuery } from '@/features/serviceEmployees';
import {
  subdivisionsApi,
  useGetSubdivisionsInfiniteQuery,
} from '@/features/subdivisions/subdivisionsApi';
import { FiltersGrid } from '@/features/types';
import { CreateSubdivisionFormModal } from '@/forms/CreateSubdivisionForm/CreateSubdivisionFormModal';
import { validateCodeFn } from '@/forms/utils/validateCodeFn';

/**
 * The people a ГРМ's представник and a підрозділ's представник and підписант are picked from: BMS's employees of the
 * page's філія (`:orgId`), as those persons are BMS's data (PO, 2026-10-30). A ГРМ has no підписант (PO, 2026-10-31,
 * GSB-8532).
 */
const useGetBranchBmsEmployeesInfiniteQuery = (
  query: FiltersGrid,
  options?: { skip?: boolean },
) => {
  const { orgId = '' } = useParams<{ orgId: string }>();

  return useGetServiceEmployeesInfiniteQuery(
    { ...query, serviceId: SERVICE_IDS.BMS, organizationId: orgId },
    { ...options, skip: options?.skip || !orgId },
  );
};

interface Props {
  children: ReactNode;
  isMainOrganizationView?: boolean;
}

/**
 * The legacy organization pages of @gasbi/shared/web/Organization that the admin mounts: the ГРМ card (created and
 * edited only here, PO, 2026-10-27), its structural subdivisions and the roles, over gdn-organizations,
 * structural-subdivisions and the shared RBAC roles.
 */
export const OrganizationApiContextWrapper = ({
  children,
  isMainOrganizationView = false,
}: Props) => {
  const dispatch = useDispatch();

  const value = useMemo(
    () => ({
      organizationsApi: {
        ...organizationsApi,
        useGetOrganizationRolesInfiniteQuery,
        useGetCachedOrganizationByIdSelector,
        useGetOrganizationsInfiniteQuery,
      },
      mainOrganizationApi: {
        ...mainOrganizationApi,
        useGetMainOrganizationByIdQuery: () => useGetAdminOrganizationQuery(),
        useGetMainOrganizationRolesInfiniteQuery,
      },
      rolesApi: {
        useGetSystemRolesQuery,
        useGetRoleByIdQuery,
        useGetCachedRoleByIdSelector,
        useAssignRoleMutation,
        useSuspendRoleMutation,
        useSaveRoleMutation,
        useGetRolePermissionsByIdQuery,
        useGetRoleAvailablePermissionsByIdQuery,
      },
      employeesApi: {
        useActivateEmployeeMutation,
        useGetEmployeesByRoleInfiniteQuery: isMainOrganizationView
          ? useGetRoleEmployeesInfiniteQuery
          : useGetEmployeesByRoleInfiniteQuery,
        useGetEmployeesByRoleQuery: isMainOrganizationView
          ? useGetRoleEmployeesListQuery
          : useGetEmployeesByRoleQuery,
        useGetEmployeesAccessibleByOrganizationQuery,
        // The representative and signatory pickers
        useGetEmployeesInfiniteQuery: useGetBranchBmsEmployeesInfiniteQuery,
        useGetDepartmentsIInfiniteQuery: () => {},
        useGetEmployeePositionsIInfiniteQuery: () => {},
      },
      subdivisionsApi: {
        ...subdivisionsApi,
        useGetSubdivisionsInfiniteQuery,
      },
      common: {
        RootRoleId,
        isRootRole,
        getRoleLabel,
        getRoleType,
      },
      utils: {
        invalidateOrgRolesTags: () =>
          dispatch(organizationsApi.util.invalidateTags(['Roles'])),
        invalidateMainOrgRolesTags: () =>
          dispatch(mainOrganizationApi.util.invalidateTags(['Roles'])),
        invalidateEmployeesByRoleTags: () => {
          dispatch(employeesApi.util.invalidateTags(['EmployeesByRole']));
          dispatch(roleEmployeesApi.util.invalidateTags(['EmployeesByRole']));
        },
        invalidateUserPermissionsTags: () =>
          dispatch(permissionsApi.util.invalidateTags(['UserPermissions'])),
      },
      getOrganizationURL: () => {},
      getMainOrganizationURL: () => {},
      validateCodeFn,
      ownsOrganizationCard: true,
      // Keeps the persons of the shared legacy cards out; the admin mounts its own ГРМ and підрозділ cards, which show
      // the ГРМ's representative and the підрозділ's representative and signatory (GSB-8522, GSB-8532).
      withoutServiceEmployees: true,
      // The «Співробітники» tab of a підрозділ assigns філія employees roles on the node (GSB-8373).
      withSubdivisionEmployeesTab: true,
      // The service chosen on that tab is the page title (its logo), as in the design (GSB-8462).
      ServiceLogo,
      // The whole structure as a tree, with a safe parent picker (GSB-8373); other hosts keep the flat grid.
      subdivisionsAsTree: true,
      // «Додати підрозділ» in steps, as the ГРМ card (GSB-8433).
      createSubdivisionForm: CreateSubdivisionFormModal,
    }),

    [dispatch, isMainOrganizationView],
  );

  return (
    // @ts-ignore
    <OrganizationApiProvider value={value}>{children}</OrganizationApiProvider>
  );
};
